import React, {
 useState, useMemo
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Trash2, Sparkles, Loader2
} from 'lucide-react';
import {
 TriggerCondition, DataChangeEvent
} from '../../types/trigger.types';

interface ConditionBuilderProps {
    conditions: TriggerCondition[];
    onChange: (conditions: TriggerCondition[]) => void;
    schemaColumns: string[];
    eventType: DataChangeEvent | null;
}

const operators = [
    {
 value: 'equals',
label: 'equals'
},
    {
 value: 'not_equals',
label: 'not equals'
},
    {
 value: 'was_changed',
label: 'was changed'
},
    {
 value: 'greater_than',
label: 'greater than'
},
    {
 value: 'less_than',
label: 'less than'
},
    {
 value: 'contains',
label: 'contains'
},
];

let condIdCounter = 0;

function mockAIConditions(prompt: string, columns: string[]): TriggerCondition[] {
    const lower = prompt.toLowerCase();
    const results: TriggerCondition[] = [];

    // Look for "status" + "changed to" pattern
    const statusMatch = lower.match(/status.*(?:changed to|equals?|is)\s+['"]?(\w+)['"]?/);
    if (statusMatch) {
        condIdCounter += 1;
        results.push({
            id: `cond_ai_${Date.now()}_${condIdCounter}`,
            field: 'record_after.status',
            operator: 'equals',
            value: statusMatch[1].toUpperCase(),
        });
    }

    // Look for "total" or "amount" > N
    const amountMatch = lower.match(/(?:total|amount|price).*(?:more than|greater than|above|>)\s*\$?(\d+)/);
    if (amountMatch) {
        condIdCounter += 1;
        const fieldName = columns.find((c) => c.includes('total') || c.includes('amount') || c.includes('price')) || 'total_amount';
        results.push({
            id: `cond_ai_${Date.now()}_${condIdCounter}`,
            field: `record_after.${fieldName}`,
            operator: 'greater_than',
            value: amountMatch[1],
        });
    }

    // Default if nothing matched
    if (results.length === 0 && columns.length > 0) {
        condIdCounter += 1;
        results.push({
            id: `cond_ai_${Date.now()}_${condIdCounter}`,
            field: `record_after.${columns[0]}`,
            operator: 'equals',
            value: '',
        });
    }

    return results;
}

const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
    conditions, onChange, schemaColumns, eventType,
}) => {
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Build available fields based on event type
    const availableFields = useMemo(() => {
        if (!eventType) return schemaColumns;
        if (eventType === 'UPDATE') {
            const fields: string[] = [];
            schemaColumns.forEach((col) => {
                fields.push(`record_before.${col}`);
                fields.push(`record_after.${col}`);
            });
            return fields;
        }
        // CREATE → record_after.*, DELETE → record_before.*
        const prefix = eventType === 'CREATE' ? 'record_after' : 'record_before';
        return schemaColumns.map((col) => `${prefix}.${col}`);
    }, [schemaColumns, eventType]);

    const addCondition = () => {
        condIdCounter += 1;
        const newCond: TriggerCondition = {
            id: `cond_${Date.now()}_${condIdCounter}`,
            field: '',
            operator: 'equals',
            value: '',
        };
        onChange([...conditions, newCond]);
    };

    const updateCondition = (index: number, update: Partial<TriggerCondition>) => {
        const newConditions = conditions.map((c, i) => (i === index ? {
 ...c,
...update
} : c));
        onChange(newConditions);
    };

    const removeCondition = (index: number) => {
        onChange(conditions.filter((_, i) => i !== index));
    };

    const handleAIGenerate = () => {
        if (!aiPrompt.trim()) return;
        setIsGenerating(true);
        setTimeout(() => {
            const generated = mockAIConditions(aiPrompt, schemaColumns);
            onChange([...conditions, ...generated]);
            setIsGenerating(false);
            setAiPrompt('');
        }, 1500);
    };

    return (
        <div className="space-y-4">
            {/* AI Assist */}
            <div className="space-y-2 p-4 border rounded-md bg-muted/20">
                <Label className="text-sm flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    Describe the conditions...
                </Label>
                <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="e.g., Only run this if the order's status was changed to 'PAID' and the total is more than $100."
                    rows={3}
                    className="resize-none"
                />
                <Button
                    size="sm"
                    onClick={handleAIGenerate}
                    disabled={!aiPrompt.trim() || isGenerating}
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                            Generate Conditions
                        </>
                    )}
                </Button>
            </div>

            {/* Manual Condition Rows */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Conditions (AND)</Label>
                    <Button variant="ghost" size="sm" onClick={addCondition} className="h-7 text-xs">
                        <Plus className="mr-1 h-3 w-3" /> Add Condition
                    </Button>
                </div>

                {conditions.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">
                        No conditions defined. The trigger will fire on every matching event.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {conditions.map((cond, idx) => (
                            <div key={cond.id} className="flex items-center gap-2">
                                {idx > 0 && (
                                    <Badge variant="outline" className="text-[10px] shrink-0 w-10 justify-center">
                                        AND
                                    </Badge>
                                )}
                                {idx === 0 && <div className="w-10 shrink-0" />}
                                <Select
                                    value={cond.field}
                                    onValueChange={(v) => updateCondition(idx, {
 field: v
})}
                                >
                                    <SelectTrigger className="flex-1 min-w-0">
                                        <SelectValue placeholder="Field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableFields.map((f) => (
                                            <SelectItem key={f} value={f}>{f}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select
                                    value={cond.operator}
                                    onValueChange={(v) => updateCondition(idx, {
 operator: v
})}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {operators.map((op) => (
                                            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {cond.operator !== 'was_changed' && (
                                    <Input
                                        value={cond.value}
                                        onChange={(e) => updateCondition(idx, {
 value: e.target.value
})}
                                        placeholder="Value"
                                        className="flex-1 min-w-0"
                                    />
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 shrink-0"
                                    onClick={() => removeCondition(idx)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConditionBuilder;
